import type { FetchRequestError } from "@lib/errors";
import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import type { SHARED_CONSTANTS_CONVERSATIONAL_WORKFLOW_ERROR_CODES } from "@lib/shared_constants";

export const knowledgeSourceToolType = "knowledge_source" as const;
export const workflowAttributeSetterToolType = "workflow_attribute_setter" as const;
export const workflowAttributeGetterToolType = "workflow_attribute_getter" as const;
export const logCustomEventToolType = "log_custom_event" as const;
export const setCustomAttributeToolType = "set_custom_attribute" as const;
export const getCustomAttributeToolType = "get_custom_attribute" as const;

export interface KnowledgeSourceToolConfig {
  tool_type: typeof knowledgeSourceToolType;
  definition_id: string;
  excluded_fields: string[];
}

export interface WorkflowAttributeSetterToolConfig {
  tool_type: typeof workflowAttributeSetterToolType;
  definition_id: string;
}

export interface WorkflowAttributeGetterToolConfig {
  tool_type: typeof workflowAttributeGetterToolType;
  definition_id: string;
}

export const logCustomEventPropertySources = ["agent", "workflow_attribute", "custom_attribute"] as const;
export type LogCustomEventPropertySource = (typeof logCustomEventPropertySources)[number];

export const setCustomAttributeSources = ["agent", "workflow_attribute"] as const;
export type SetCustomAttributeSource = (typeof setCustomAttributeSources)[number];

export interface LogCustomEventProperty {
  name: string;
  source: LogCustomEventPropertySource;
  source_id: string | null;
  description: string | null;
  required: boolean;
}

export interface LogCustomEventToolConfig {
  tool_type: typeof logCustomEventToolType;
  marker_id: string;
  event_name: string;
  description: string;
  properties: LogCustomEventProperty[];
}

export interface SetCustomAttributeToolConfig {
  tool_type: typeof setCustomAttributeToolType;
  marker_id: string;
  attribute_name: string;
  description: string;
  source: SetCustomAttributeSource;
  source_id: string | null;
}

export interface GetCustomAttributeToolConfig {
  tool_type: typeof getCustomAttributeToolType;
  marker_id: string;
  attribute_name: string;
}

export type ToolConfig =
  | KnowledgeSourceToolConfig
  | WorkflowAttributeSetterToolConfig
  | WorkflowAttributeGetterToolConfig
  | LogCustomEventToolConfig
  | SetCustomAttributeToolConfig
  | GetCustomAttributeToolConfig;

export interface ConversationalWorkflowStep {
  instruction: string;
  tool_configs: ToolConfig[];
}

export interface OperatorSafeConversationalWorkflowStep {
  instruction: string;
}

export interface ConversationalWorkflowChatSetting {
  app_ids: string[];
}

export interface ConversationalWorkflowMessagingChannelSetting {
  subscription_group_ids: string[];
}

export interface ConversationalWorkflowResultObject {
  id: string;
  name: string;
  purpose: string;
  steps: ConversationalWorkflowStep[];
  chat_setting: ConversationalWorkflowChatSetting | null;
  messaging_channel_setting: ConversationalWorkflowMessagingChannelSetting | null;
  created_by_email: string;
  created_at: string;
  updated_at: string;
}

export interface ConversationalWorkflowResult {
  result: ConversationalWorkflowResultObject;
}

export interface OperatorSafeConversationalWorkflowResultObject {
  id: string;
  name: string;
  purpose: string;
  step_count: number;
  steps: OperatorSafeConversationalWorkflowStep[];
}

export interface OperatorSafeConversationalWorkflowResult {
  result: OperatorSafeConversationalWorkflowResultObject;
}

export type GetConversationalWorkflowResult =
  | ConversationalWorkflowResult
  | OperatorSafeConversationalWorkflowResult;

export interface GetConversationalWorkflowProps {
  appGroupId: string;
  workflowId: string;
}

export interface ConversationalWorkflowPayload {
  id?: string;
  name: string;
  purpose: string;
  steps: ConversationalWorkflowStep[];
  chat_setting: ConversationalWorkflowChatSetting;
  messaging_channel_setting: ConversationalWorkflowMessagingChannelSetting;
}

export interface PostConversationalWorkflowProps extends ConversationalWorkflowPayload {
  appGroupId: string;
}

export interface PutConversationalWorkflowProps extends ConversationalWorkflowPayload {
  appGroupId: string;
  workflowId: string;
}

export interface PostConversationalPreviewWorkflowProps {
  appGroupId: string;
  sessionId: string;
  userMessage: string;
  userId: string;
  conversationalWorkflowPayload: ConversationalWorkflowPayload;
}

export type PostConversationalPreviewWorkflowErrors =
  | FetchUserEncodedError<SHARED_CONSTANTS_CONVERSATIONAL_WORKFLOW_ERROR_CODES["SESSION_ID_REQUIRED"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_CONVERSATIONAL_WORKFLOW_ERROR_CODES["USER_MESSAGE_REQUIRED"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_CONVERSATIONAL_WORKFLOW_ERROR_CODES["USER_ID_REQUIRED"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_CONVERSATIONAL_WORKFLOW_ERROR_CODES["USER_ID_INVALID"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_CONVERSATIONAL_WORKFLOW_ERROR_CODES["ID_REQUIRED"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_CONVERSATIONAL_WORKFLOW_ERROR_CODES["NOT_FOUND"]>;

export const isPostConversationalPreviewWorkflowError = (
  error: FetchRequestError
): error is PostConversationalPreviewWorkflowErrors => {
  return error instanceof FetchUserEncodedError;
};

export interface PostConversationalPreviewWorkflowResult {
  message_id: string;
}
